import { formatMoney, fetchPriceData } from '@theme/theme-currency';
import { localizeUrl } from '@theme/localize-url';

class CompareProducts extends HTMLElement {
  constructor() {
    super();
    this.STOREFRONT_TOKEN = Theme.otterVars?.storefrontToken || "";
    this.GRAPHQL_URL = `https://${window.location.hostname}/api/2024-01/graphql.json`;
    this.limit = 3;
    this.cachedVariants = [];
    this.productsHolder = this.querySelector('.js-compare-products-holder');
    this.mobileCardsHolder = this.querySelector('.js-mobile-cards');
    this.placeholderImage = this.getAttribute('data-placeholder-src');
    this.tooltips = JSON.parse(document.querySelector('#ComparisonTooltips')?.textContent || '{}');
    this.yotpoWidgetId = Theme.otterVars?.yotpoWidgetId || null;

    this.currencySettings = {
      currencyCodeEnabled: window.OtterVars.MarketSettings?.currencyCodeEnabled,
      moneyWithCurrencyFormat: window.OtterVars.MarketSettings?.moneyWithCurrencyFormat
    };

    this.add_product = Theme.translations?.add_compare_product || '';
    this.add_product_button = Theme.translations?.add_compare_product_button || '';
    this.no_products_selected = Theme.translations?.no_products_selected || '';
    this.drop_rating = Theme.translations?.drop_rating || '';
    this.rating = Theme.translations?.rating || '';
    this.thickness = Theme.translations?.thickness || '';
    this.color = Theme.translations?.color || '';
    this.case_design = Theme.translations?.case_design || '';
    this.features = Theme.translations?.features || '';
    this.materials = Theme.translations?.materials || '';
    this.product_details = Theme.translations?.product_details || '';
    this.certified = Theme.translations?.certified || '';
    this.shop_now = Theme.translations?.shop_now_button || '';
  }

  async connectedCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const ids = urlParams.get('ids')?.split(',').filter(id => id.trim() !== "") || [];

    if (ids.length === 0) {
      this.renderEmptyState();
      return;
    }

    const gids = ids.map(id => id.includes('gid://') ? id : `gid://shopify/ProductVariant/${id}`);
    this.renderLoading();

    try {
      this.cachedVariants = await this.fetchComparisonData(gids);
      this.renderTable(this.cachedVariants);
    } catch (err) {
      this.renderEmptyState();
    }

    this.initStickyObserver();
    this.initSlider();
    window.addEventListener('resize', this.initSlider.bind(this));
    this.updateBreadcrumbs();
  }

  async fetchComparisonData(variantIds) {
    const countryCode = Shopify.country || "GB";
    const query = `
      query getComparison($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            title
            shortFeatures: metafield(namespace: "custom", key: "short_features_description") { value }
            materials: metafield(namespace: "custom", key: "materials") { value }
            hexCode: metafield(namespace: "custom", key: "color_hex_code") { value }
            swatchImg: metafield(namespace: "custom", key: "color_swatch") {
              reference { ... on MediaImage { image { url } } }
            }
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            image { url altText }
            product {
              id
              title
              handle
              protection: metafield(namespace: "custom", key: "product_profile_rating") { value }
              certified: metafield(namespace: "custom", key: "ul_certified") { value }
              thickness: metafield(namespace: "custom", key: "product_drop_rating") { value }
              case_design: metafield(namespace: "custom", key: "case_design") { value }
              variants(first: 20) {
                nodes {
                  id
                  hexCode: metafield(namespace: "custom", key: "color_hex_code") { value }
                  swatchImg: metafield(namespace: "custom", key: "color_swatch") {
                    reference { ... on MediaImage { image { url } } }
                  }
                }
              }
            }
          }
        }
      }
    `;
    const response = await fetch(this.GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables: { ids: variantIds, country: countryCode } }),
    });
    const result = await response.json();
    return (result.data?.nodes || []).filter(n => n !== null);
  }

  generateRatingMarkup(productGid) {
    if (!productGid) return '';
    const numericId = productGid.split('/').pop();
    return `<div class="yotpo-widget-instance" data-yotpo-instance-id="${this.yotpoWidgetId}" data-yotpo-product-id="${numericId}"></div>`;
  }

  renderLoading() {
    if (this.productsHolder) {
      this.productsHolder.innerHTML = `<div class="compare-loader">Loading...</div>`;
    }
  }

  renderEmptyState() {
    if (this.productsHolder) {
      this.productsHolder.innerHTML = `<div class="compare-empty-state"><p>${this.no_products_selected}</p></div>`;
    }
  }

  renderTable(variants) {
    if (variants.length === 0) {
      this.renderEmptyState();
      return;
    }

    const attributes = [
      { label: this.rating, key: 'yotpo_rating' },
      { label: this.drop_rating, key: 'protection' },
      { label: this.thickness, key: 'thickness' },
      { label: this.color, key: 'colorsList' },
      { label: this.case_design, key: 'case_design' },
      { label: this.features, key: 'shortFeatures' },
      { label: this.certified, key: 'certified' },
      { label: this.materials, key: 'materials' }
    ];

    const activeAttributes = attributes.filter(attr => {
      if (attr.key === 'colorsList' || (attr.key === 'yotpo_rating' && this.yotpoWidgetId != null)) return true;
      return variants.some(v => (v.product?.[attr.key]?.value || v[attr.key]?.value));
    });

    let mobileHtml = `<div class="compare-products__grid">`;
    variants.forEach((variant) => {
      const shortId = variant.id.split('/').pop();
      const variantUrl = `/products/${variant.product.handle}?variant=${shortId}`;
      const updatedUrl = localizeUrl(variantUrl);

      mobileHtml += `
        <div class="compare-products__mobile-sticky-card">
          <p class="compare-products__item-title">${variant.product.title}</p>
          <p class="compare-products__item-price">${this.generatePriceMarkup(variant)}</p>
          <a href="${updatedUrl}" class="button button-primary">${this.shop_now}</a>
        </div>
      `;
    });

    for (let i = variants.length; i < this.limit; i++) {
      mobileHtml += `
        <div class="compare-products__mobile-sticky-card compare-products__mobile-sticky-card--empty">
          <div class="compare-products__placeholder">
            <span class="plus"><svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24 8V40M8 24H40" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
            <p>${this.add_product}</p>
          </div>
          <button class="button button-secondary button--full-width" onclick="window.history.back()">${this.add_product_button}</button>
        </div>
      `;
    }
    mobileHtml += `</div>`;
    if(this.mobileCardsHolder) this.mobileCardsHolder.innerHTML = mobileHtml;

    let html = `<div class="compare-products__grid js-main-compare-grid">`;
    variants.forEach((variant, colIndex) => {
      const shortId = variant.id.split('/').pop();
      const variantUrl = `/products/${variant.product.handle}?variant=${shortId}`;
      const updatedUrl = localizeUrl(variantUrl);
      const colNum = colIndex + 1;
      const variantImage = variant.image?.url || this.placeholderImage;

      html += `
        <div class="compare-products__cell compare-products__cell--header" style="grid-column: ${colNum}; grid-row: 1;">
          <div class="compare-products__header-inner">
            <div class="compare-products__header-card">
              <button class="compare-products__remove-btn js-remove-variant" data-id="${variant.id}">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 6H5.5M20 6H18.5M9.5 6V3H14.5V6M9.5 6H14.5M9.5 6H5.5M14.5 6H18.5M5.5 6L6.5 20H17.5L18.5 6M10 8.5L10.3 17.5M14 8.5L13.8 17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="compare-products__image-wrap">
                <img class="compare-products__image" src="${variantImage}" alt="${variant.title}">
              </div>
              <div class="compare-products__header-meta">
                <p class="compare-products__item-title">${variant.product.title}</p>
                <p class="compare-products__item-price">${this.generatePriceMarkup(variant)}</p>
              </div>
            </div>
            <a href="${updatedUrl}" class="button button-primary compare-products__shop-btn">${this.shop_now}</a>
          </div>
        </div>
        <div class="compare-products__cell compare-products__cell--title" style="grid-column: ${colNum}; grid-row: 2;">
            <h2 class="compare-products__section-heading">${colIndex === 0 ? this.product_details : ''}</h2>
        </div>`;

      activeAttributes.forEach((attr, attrIndex) => {
        const tooltipKey = attr.label.toLowerCase().trim();
        const hasTooltip = this.tooltips[tooltipKey];
        html += `
          <div class="compare-products__cell compare-products__cell--data" data-row="${attr.key}" style="grid-column: ${colNum}; grid-row: ${attrIndex + 3};">
            <div class="compare-products__row-header">
              <span class="compare-products__row-label">${attr.label}</span>
              ${hasTooltip ? `
                <div class="tooltip-wrapper">
                  <button type="button" class="tooltip-trigger js-tooltip-trigger">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                  </button>
                  <div class="tooltip-content">
                    ${this.tooltips[tooltipKey]}
                    <button class="tooltip-close js-tooltip-close"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 6.5L17.5 17.5M6.5 17.5L17.5 6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
                  </div>
                </div>
              ` : ''}
            </div>
            <div class="compare-products__row-value">${this.processAttribute(variant, attr)}</div>
          </div>`;
      });
    });

    for (let i = variants.length; i < this.limit; i++) {
      const colNum = i + 1;
      html += `
        <div class="compare-products__cell compare-products__cell--header compare-products__cell--empty" style="grid-column: ${colNum}; grid-row: 1;">
          <div class="compare-products__header-inner">
            <div class="compare-products__placeholder">
              <span class="plus"><svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24 8V40M8 24H40" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
              <p>${this.add_product}</p>
            </div>
            <button class="button button-secondary compare-products__add-btn" onclick="window.history.back()">${this.add_product_button}</button>
          </div>
        </div>
        <div class="compare-products__cell compare-products__cell--title" style="grid-column: ${colNum}; grid-row: 2;"></div>`;
      activeAttributes.forEach((attr, attrIndex) => {
        html += `<div class="compare-products__cell compare-products__cell--data compare-products__cell--empty" style="grid-column: ${colNum}; grid-row: ${attrIndex + 3};"></div>`;
      });
    }

    html += `</div>`;
    if (this.productsHolder) this.productsHolder.innerHTML = html;
    this.bindEvents();

    requestAnimationFrame(() => {
      if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
        window.yotpo.refreshWidgets();
      } else if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
        window.yotpoWidgetsContainer.initWidgets();
      }
    });
  }

  renderSwatches(variant) {
    const nodes = variant.product?.variants?.nodes || [];
    return `<div class="compare-products__swatch-list">${nodes.map(v => {
      const imgUrl = v.swatchImg?.reference?.image?.url;
      const style = imgUrl ? `background-image:url(${imgUrl})` : `background-color:${v.hexCode?.value || '#ccc'}`;
      return `<span class="compare-products__swatch" style="${style}"></span>`;
    }).join('')}</div>`;
  }

  processAttribute(variant, attr) {
    if (attr.key === 'colorsList') return this.renderSwatches(variant);
    if (attr.key === 'yotpo_rating' && this.yotpoWidgetId != null) return this.generateRatingMarkup(variant.product.id);

    const val = variant.product?.[attr.key]?.value || variant[attr.key]?.value;
    if (attr.key === 'protection') return this.render7DotMeter(val, '1x', '7x', this.drop_rating);
    if (attr.key === 'thickness') return this.render7DotMeter(val, 'Thin', 'Thick', this.thickness);
    if (attr.key === 'shortFeatures' || attr.key === 'materials') return this.renderList(val);
    return val || '—';
  }

  render7DotMeter(value, leftLabel, rightLabel, typeLabel) {
    if (!value) return '—';
    const rating = parseInt(value);
    let dotsHtml = '';
    for (let i = 1; i <= 7; i++) dotsHtml += `<li class="${i <= rating ? 'active-dot' : ''}">${typeLabel} ${i}</li>`;
    return `<div class="product-rating-wrapper"><div class="control"><div class="row"><span class="left">${leftLabel}</span><ul class="dots">${dotsHtml}</ul><span class="right">${rightLabel}</span></div></div></div>`;
  }

  renderList(val) {
    if (!val) return '—';
    try {
      const list = val.includes('[') ? JSON.parse(val) : val.split(';');
      return `<ul>${list.map(item => `<li>${item}</li>`).join('')}</ul>`;
    } catch(e) { return val; }
  }

  bindEvents() {
    this.querySelectorAll('.js-remove-variant').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idToRemove = btn.dataset.id;
        const shortIdToRemove = idToRemove.split('/').pop();
        this.cachedVariants = this.cachedVariants.filter(v => v.id !== idToRemove);
        let storageProducts = JSON.parse(localStorage.getItem('compareProducts') || '[]');
        storageProducts = storageProducts.filter(p => p.id !== shortIdToRemove && p.id !== idToRemove);
        localStorage.setItem('compareProducts', JSON.stringify(storageProducts));
        const url = new URL(window.location.href);
        const newIds = this.cachedVariants.map(v => v.id.split('/').pop()).join(',');
        if (newIds) url.searchParams.set('ids', newIds); else url.searchParams.delete('ids');
        window.history.replaceState({}, '', url);
        this.renderTable(this.cachedVariants);
        document.dispatchEvent(new CustomEvent('theme:compare:update'));
      });
    });
    this.querySelectorAll('.js-tooltip-trigger').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const wrapper = btn.closest('.tooltip-wrapper');
        this.querySelectorAll('.tooltip-wrapper').forEach(w => { if (w !== wrapper) w.classList.remove('is-active'); });
        wrapper.classList.toggle('is-active');
      });
    });
    this.querySelectorAll('.js-tooltip-close').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        btn.closest('.tooltip-wrapper').classList.remove('is-active');
      });
    });
    document.addEventListener('click', () => {
      this.querySelectorAll('.tooltip-wrapper').forEach(w => w.classList.remove('is-active'));
    });
  }

  initStickyObserver() {
    const headerGroup = document.querySelector('#header-group');
    const headerHeight = headerGroup?.offsetHeight || 0;
    const sentinel = document.querySelector('.sticky-sentinel');
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < headerHeight);
      });
    }, { threshold: [0], rootMargin: `-${headerHeight}px 0px 0px 0px` });
    observer.observe(sentinel);
  }

  initSlider() {
    const nextBtns = this.querySelectorAll('.js-arrow-next');
    const prevBtns = this.querySelectorAll('.js-arrow-prev');
    const mainGrid = this.querySelector('.js-main-compare-grid');
    const stickyGrid = this.querySelector('.js-mobile-cards .compare-products__grid');
    let isSyncing = false;
    let leader = null;
    let syncTimeout = null;

    const updateButtons = () => {
      if (!mainGrid) return;
      const scrollLeft = Math.ceil(mainGrid.scrollLeft);
      const maxScroll = mainGrid.scrollWidth - mainGrid.clientWidth;
      prevBtns.forEach(btn => btn.disabled = scrollLeft <= 5);
      nextBtns.forEach(btn => btn.disabled = scrollLeft >= maxScroll - 5);
    };

    const sync = (source, target) => {
      if (isSyncing || leader !== source) return;
      isSyncing = true;
      requestAnimationFrame(() => {
        if (target) target.scrollLeft = source.scrollLeft;
        isSyncing = false;
      });
    };

    const setLeader = (el) => {
      leader = el;
      clearTimeout(syncTimeout);
      syncTimeout = setTimeout(() => { leader = null; }, 1000);
    };

    [mainGrid, stickyGrid].forEach(el => {
      if (!el) return;
      el.addEventListener('touchstart', () => setLeader(el), { passive: true });
      el.addEventListener('mousedown', () => setLeader(el), { passive: true });
      el.addEventListener('scroll', () => {
        if (el === mainGrid) { sync(mainGrid, stickyGrid); updateButtons(); }
        else { sync(stickyGrid, mainGrid); }
      }, { passive: true });
    });

    const handleBtnScroll = (direction) => {
      if (!mainGrid) return;
      leader = mainGrid;
      const cardWidth = window.innerWidth * 0.45;
      const gap = 8;
      mainGrid.scrollBy({ left: (cardWidth + gap) * direction, behavior: 'smooth' });
    };

    nextBtns.forEach(btn => btn.addEventListener('click', () => handleBtnScroll(1)));
    prevBtns.forEach(btn => btn.addEventListener('click', () => handleBtnScroll(-1)));

    setTimeout(updateButtons, 200);
  }

  generatePriceMarkup(variant) {
    if (!variant.price) return '';
    const priceAmount = parseFloat(variant.price.amount);
    const compareAtAmount = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null;

    const priceFormatted = formatMoney(priceAmount, this.currencySettings);

    if (compareAtAmount && compareAtAmount > priceAmount) {
      const comparePriceFormatted = formatMoney(compareAtAmount, this.currencySettings);
      return `<span class="compare-products__item-price--compare">${comparePriceFormatted}</span>
              <span class="compare-products__item-price--sale">${priceFormatted}</span>`;
    }
    return `<span class="compare-products__item-price-main">${priceFormatted}</span>`;
  }

  updateBreadcrumbs() {
    const breadcrumbData = localStorage.getItem('compareBreadcrumb');
    const backLink = this.querySelector('.js-compare-back-link');
    const backCollectionText = this.querySelector('.js-compare-back-collection');
    if (breadcrumbData && backLink && backCollectionText) {
      try {
        const data = JSON.parse(breadcrumbData);
        backLink.href = localizeUrl(data.url);
        backCollectionText.textContent = data.title;
      } catch (e) { console.error(e); }
    }
  }
}

customElements.define('compare-products', CompareProducts);